import { translate } from '@fiverr-private/i18n-react';
import { PortfolioModalProjectFullData } from '../../server';
import { buildSubCategoryTKey } from '../../i18n';
import {
    PortfolioProjectCardAttachment,
    PortfolioProjectCardProject,
    PortfolioProjectCardTags,
} from '../../components';
import { mapCategoryDetails, mapInternalProjectDetails } from '../internalProject';

export const selectPortfolioProjectCardAttachments = ({
    items,
}: Partial<Pick<PortfolioModalProjectFullData, 'items'>>): PortfolioProjectCardAttachment[] | undefined => {
    if (!items?.nodes?.length) {
        return;
    }

    return items.nodes
        .map(({ attachment }) => {
            if (!attachment?.previewUrl || !attachment?.contentType) {
                return undefined;
            }

            return {
                previewUrl: attachment.previewUrl,
                mediaUrl: attachment.mediaUrl || undefined,
                type: attachment.contentType,
            } satisfies PortfolioProjectCardAttachment;
        })
        .filter(Boolean) as PortfolioProjectCardAttachment[];
};

export const selectPortfolioProjectCardTags = ({
    subCategoryIds,
    industries,
    services,
    metadataTypes,
}: Partial<Pick<PortfolioModalProjectFullData, 'subCategoryIds' | 'industries' | 'services' | 'metadataTypes'>>) =>
    ([] as PortfolioProjectCardTags[]).concat(
        industries?.map(({ id, name }) => ({ id, name })) || [],
        getValuableCategories({ subCategoryIds, services }),
        metadataTypes
            ?.map(({ values }) => values)
            .flat()
            .map(({ id, name }) => ({ id, name })) || []
    );

const getValuableCategories = ({
    subCategoryIds,
    services,
}: Partial<Pick<PortfolioModalProjectFullData, 'subCategoryIds' | 'services'>>) => {
    if (services?.length) {
        return services?.map(({ id, name }) => ({ id, name }));
    }

    if (subCategoryIds?.length) {
        return subCategoryIds?.map((id) => ({ id: id.toString(), name: translate<string>(buildSubCategoryTKey(id)) }));
    }

    return [];
};

export const selectPortfolioProject = ({
    title,
    source,
    description,
    items,
    price,
    projectStartedAt,
    subCategoryIds,
    industries,
    services,
    metadataTypes,
    duration,
    id,
    internalProjectDetails,
}: PortfolioModalProjectFullData): PortfolioProjectCardProject => ({
    id,
    title: title || undefined,
    source,
    description: description || undefined,
    price: price?.amount || undefined,
    startedAt: projectStartedAt ? projectStartedAt * 1000 : undefined,
    attachments: selectPortfolioProjectCardAttachments({ items }),
    duration: duration || undefined,
    tags: selectPortfolioProjectCardTags({ subCategoryIds, industries, services, metadataTypes }),
    internalProjectDetails: mapInternalProjectDetails(internalProjectDetails) || undefined,
    categoryDetails: mapCategoryDetails(subCategoryIds, internalProjectDetails?.nestedSubCategoryId || undefined),
});
